import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Filter, RefreshCw, Download, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import FAVoidTickets from "./FAVoidTickets";
import FARefundFailures from "./FARefundFailures";
import RefundAmountCell from "../../components/shared/RefundAmountCell";

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// iter197: shared download-xlsx button for every analytics table. Hits
// `/ticketing/fa/analytics/export` with the current scope (date range +
// event filter) + the requested `view` so the Excel matches on-screen.
function DownloadAnalyticsBtn({ view, params, testid }) {
  const { authAxios } = useAuth();
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const q = new URLSearchParams(params);
      q.set("view", view);
      const res = await authAxios.get(`/ticketing/fa/analytics/export?${q.toString()}`, { responseType: "blob" });
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      downloadBlob(res.data, `analytics_${view}_${ts}.xlsx`);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
      title="Download this table as Excel"
    >
      <Download className="w-3 h-3" />
      <span>Excel</span>
    </button>
  );
}

export default function FATickets() {
  const [view, setView] = useState("analytics");
  return (
    <div data-testid="fa-tickets">
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setView("analytics")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${view === "analytics" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border border-slate-200"}`} data-testid="fa-tickets-view-analytics">Analytics</button>
        <button onClick={() => setView("per-event")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${view === "per-event" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border border-slate-200"}`} data-testid="fa-tickets-view-per-event">Per-Event</button>
        <button onClick={() => setView("void")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${view === "void" ? "bg-red-600 text-white" : "bg-white text-red-600 border border-red-200 hover:bg-red-50"}`} data-testid="fa-tickets-view-void">Void</button>
        <button onClick={() => setView("refund-failures")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${view === "refund-failures" ? "bg-amber-600 text-white" : "bg-white text-amber-700 border border-amber-200 hover:bg-amber-50"}`} data-testid="fa-tickets-view-refund-failures">Refund Failures</button>
      </div>
      {view === "analytics" && <FAAnalyticsView />}
      {view === "per-event" && <FAPerEventView />}
      {view === "void" && <FAVoidTickets />}
      {view === "refund-failures" && <FARefundFailures />}
    </div>
  );
}

function FAAnalyticsView() {
  const [days, setDays] = useState(30);
  const [customMode, setCustomMode] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [eventId, setEventId] = useState("");
  const [events, setEvents] = useState([]);
  const [data, setData] = useState(null);
  const { authAxios } = useAuth();

  // Fetch the events list once for the picker dropdown. Sorted by start_date
  // desc so recent events are at the top of the list.
  useEffect(() => {
    authAxios.get("/events").then(r => {
      const list = (r.data || []).slice().sort((a, b) =>
        (b.start_date || "").localeCompare(a.start_date || "")
      );
      setEvents(list);
    }).catch(() => setEvents([]));
  }, [authAxios]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (customMode && startDate && endDate) {
      p.set("start_date", startDate); p.set("end_date", endDate);
    } else {
      p.set("days", String(days));
    }
    if (eventId) p.set("event_id", eventId);
    authAxios.get(`/ticketing/fa/analytics?${p.toString()}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [days, customMode, startDate, endDate, eventId, authAxios]);
  // iter197: analyticsParams carries whatever filter is currently active so
  // every per-table "Download Excel" button hits the backend with the SAME
  // scope as the on-screen numbers.
  const analyticsParams = (() => {
    const p = new URLSearchParams();
    if (customMode && startDate && endDate) {
      p.set("start_date", startDate); p.set("end_date", endDate);
    } else {
      p.set("days", String(days));
    }
    if (eventId) p.set("event_id", eventId);
    return p.toString();
  })();
  if (!data) return <p className="text-slate-400 text-center py-8">Loading...</p>;
  const k = data.kpis;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        {[7, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => { setCustomMode(false); setDays(d); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${!customMode && days === d ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border border-slate-200"}`}
            data-testid={`fa-analytics-preset-${d}d`}
          >{d}d</button>
        ))}
        <button
          onClick={() => setCustomMode(v => !v)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium ${customMode ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border border-slate-200"}`}
          data-testid="fa-analytics-custom-toggle"
        >Custom range</button>
        {customMode && (
          <div className="flex items-center gap-2 text-xs">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200" data-testid="fa-analytics-start-date" />
            <span className="text-slate-400">→</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200" data-testid="fa-analytics-end-date" />
          </div>
        )}

        {/* Event picker dropdown — sits alongside the time-range chips so an
            FA can scope by both simultaneously ("last 30 days for Durgapuja"). */}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[11px] text-slate-500 shrink-0">Event</label>
          <select
            value={eventId}
            onChange={e => setEventId(e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs max-w-[200px]"
            data-testid="fa-analytics-event-select"
          >
            <option value="">All events</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="fa-analytics-kpi-row">
        <Kpi label="Tickets" value={k.tickets_total} />
        <Kpi label="Paid" value={k.tickets_paid} color="green" />
        <Kpi label="Used" value={k.tickets_used} color="slate" />
        <Kpi label="Refunded" value={k.tickets_refunded} color="red" />
        <Kpi label="Cancelled" value={k.tickets_cancelled || 0} color="amber" />
        <Kpi label="Revenue" value={`€${k.gross_revenue.toFixed(2)}`} color="blue" />
      </div>
      <div className="grid grid-cols-2 gap-3" data-testid="fa-analytics-channel-row">
        <Kpi label={`Online bookings · ${k.online_tickets ?? 0} tickets`} value={`€${(k.online_revenue || 0).toFixed(2)}`} color="blue" />
        <Kpi label={`Walk-in tickets · ${k.walkin_tickets ?? 0} tickets`} value={`€${(k.walkin_revenue || 0).toFixed(2)}`} color="green" />
      </div>
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#1e3a5f]">Top events</h3>
          <DownloadAnalyticsBtn view="by_event" params={analyticsParams} testid="fa-analytics-download-by-event" />
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-400"><th className="py-1">Event</th><th className="text-right">Tickets</th><th className="text-right">Revenue</th></tr></thead>
          <tbody>
            {data.by_event.slice(0, 10).map(e => (
              <tr key={e.event_id} className="border-t border-slate-100"><td className="py-2">{e.event_name}</td><td className="text-right">{e.count}</td><td className="text-right font-bold">€{e.revenue.toFixed(2)}</td></tr>
            ))}
            {data.by_event.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-slate-400">No data</td></tr>}
          </tbody>
        </table>
      </div>
      {data.by_tier && data.by_tier.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-[#1e3a5f]">By tier</h3>
            <DownloadAnalyticsBtn view="by_tier" params={analyticsParams} testid="fa-analytics-download-by-tier" />
          </div>
          <p className="text-[10px] text-slate-400 mb-2">Package sales are broken down into their component tiers. Only valid tickets counted (void, cancelled and refunded excluded).</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400"><th className="py-1">Tier</th><th className="text-right">Tickets</th><th className="text-right">From package</th><th className="text-right">% of capacity</th><th className="text-right">Revenue</th></tr></thead>
            <tbody>
              {data.by_tier.map(t => (
                <tr key={t.tier} className="border-t border-slate-100" data-testid={`fa-analytics-tier-row-${t.tier}`}>
                  <td className="py-2">{t.tier}</td>
                  <td className="text-right">{t.count}</td>
                  <td className="text-right text-slate-500">{t.from_package_count || 0}</td>
                  <td className="text-right" data-testid={`fa-analytics-tier-cap-${t.tier}`}>
                    {t.capacity_pct == null
                      ? <span className="text-slate-400" title="Unbounded capacity">—</span>
                      : <span className={t.capacity_pct >= 90 ? "text-red-600 font-semibold" : t.capacity_pct >= 70 ? "text-amber-600" : "text-slate-600"}>{t.capacity_pct}% <span className="text-[10px] text-slate-400">({t.count}/{t.capacity})</span></span>
                    }
                  </td>
                  <td className="text-right font-bold">€{t.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ByTierSplitTable rows={data.by_tier_split || []} download={<DownloadAnalyticsBtn view="by_tier_split" params={analyticsParams} testid="fa-analytics-download-by-tier-split" />} testidPrefix="fa-analytics" />
      {data.by_package && data.by_package.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="fa-analytics-by-package">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#1e3a5f]">By package</h3>
            <DownloadAnalyticsBtn view="by_package" params={analyticsParams} testid="fa-analytics-download-by-package" />
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400"><th className="py-1">Package</th><th className="text-right">Packages sold</th><th className="text-right">Ticket rows</th><th className="text-right">Revenue</th></tr></thead>
            <tbody>
              {data.by_package.map(p => (
                <tr key={p.package_id} className="border-t border-slate-100">
                  <td className="py-2">{p.name}</td>
                  <td className="text-right">{p.packages_sold}</td>
                  <td className="text-right text-slate-500">{p.ticket_rows}</td>
                  <td className="text-right font-bold">€{p.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ByVariantTable rows={data.by_variant || []} download={<DownloadAnalyticsBtn view="by_variant" params={analyticsParams} testid="fa-analytics-download-by-variant" />} />
      <ByVariantSplitTable rows={data.by_variant_split || []} download={<DownloadAnalyticsBtn view="by_variant_split" params={analyticsParams} testid="fa-analytics-download-by-variant-split" />} testidPrefix="fa-analytics" />
    </div>
  );
}

// iter166: same "member vs guest" split tables the shop admin dashboard
// uses. Kept local to avoid a cross-feature import — the two features
// stay independently deployable but the layout stays consistent.
function ByTierSplitTable({ rows, testidPrefix = "fa-analytics", download }) {
  const [expanded, setExpanded] = useState(() => new Set());
  if (!rows || rows.length === 0) return null;
  const toggle = (tier) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(tier)) next.delete(tier); else next.add(tier);
    return next;
  });
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid={`${testidPrefix}-by-tier-split`}>
      <div className="flex items-center justify-between mb-1"><h3 className="font-bold text-[#1e3a5f]">By tier — member vs guest</h3>{download}</div>
      <p className="text-[10px] text-slate-400 mb-3">Click a member count to see the member-type split.</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-400">
            <th className="py-1">Tier</th>
            <th className="text-right">Members</th>
            <th className="text-right">Member €</th>
            <th className="text-right">Guests</th>
            <th className="text-right">Guest €</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const isOpen = expanded.has(r.tier);
            const hasMemberSplit = (r.by_member_type || []).length > 0;
            return (
              <React.Fragment key={r.tier}>
                <tr className="border-t border-slate-100">
                  <td className="py-2">{r.tier}</td>
                  <td className="text-right">
                    {hasMemberSplit ? (
                      <button onClick={() => toggle(r.tier)} className="text-[#0ea5e9] hover:underline">
                        {r.member_count} {isOpen ? "▾" : "▸"}
                      </button>
                    ) : r.member_count}
                  </td>
                  <td className="text-right font-bold">€{r.member_revenue.toFixed(2)}</td>
                  <td className="text-right text-slate-500">{r.guest_count}</td>
                  <td className="text-right text-slate-500">€{r.guest_revenue.toFixed(2)}</td>
                </tr>
                {isOpen && (r.by_member_type || []).map(mt => (
                  <tr key={`${r.tier}-${mt.member_type}`} className="bg-slate-50">
                    <td className="py-1 pl-6 text-xs text-slate-500">↳ {mt.member_type}</td>
                    <td className="text-right text-xs">{mt.count}</td>
                    <td className="text-right text-xs">€{mt.revenue.toFixed(2)}</td>
                    <td colSpan={2} className="text-xs text-slate-400 pl-4">member-only</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ByVariantSplitTable({ rows, testidPrefix = "fa-analytics", download }) {
  const [expanded, setExpanded] = useState(() => new Set());
  if (!rows || rows.length === 0) return null;
  const grouped = rows.reduce((acc, r) => {
    const k = r.event_id || "";
    if (!acc[k]) acc[k] = { name: r.event_name, list: [] };
    acc[k].list.push(r);
    return acc;
  }, {});
  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid={`${testidPrefix}-by-variant-split`}>
      <div className="flex items-center justify-between mb-1"><h3 className="font-bold text-[#1e3a5f]">By tier variant — member vs guest</h3>{download}</div>
      <p className="text-[10px] text-slate-400 mb-3">Grouped by event. Member counts expand into the member-type split.</p>
      {Object.entries(grouped).map(([eid, grp]) => (
        <div key={eid} className="mb-4 last:mb-0">
          <p className="text-xs font-bold text-slate-600 mb-1">{grp.name || "(unknown event)"}</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400">
                <th className="py-1">Tier</th>
                <th>Variant</th>
                <th className="text-right">Members</th>
                <th className="text-right">Member €</th>
                <th className="text-right">Guests</th>
                <th className="text-right">Guest €</th>
              </tr>
            </thead>
            <tbody>
              {grp.list.map((r, idx) => {
                const rowKey = `${eid}-${r.tier}-${r.variant}-${idx}`;
                const isOpen = expanded.has(rowKey);
                const hasMemberSplit = (r.by_member_type || []).length > 0;
                return (
                  <React.Fragment key={rowKey}>
                    <tr className="border-t border-slate-100">
                      <td className="py-2">{r.tier}</td>
                      <td className="text-slate-500">{r.variant || "—"}</td>
                      <td className="text-right">
                        {hasMemberSplit ? (
                          <button onClick={() => toggle(rowKey)} className="text-[#0ea5e9] hover:underline">
                            {r.member_count} {isOpen ? "▾" : "▸"}
                          </button>
                        ) : r.member_count}
                        
                      </td>
                      <td className="text-right font-bold">€{Number(r.member_revenue || 0).toFixed(2)}</td>
                      <td className="text-right text-slate-500">
                        {r.guest_count}
                        
                      </td>
                      <td className="text-right text-slate-500">€{Number(r.guest_revenue || 0).toFixed(2)}</td>
                    </tr>
                    {isOpen && (r.by_member_type || []).map(mt => (
                      <tr key={`${rowKey}-${mt.member_type}`} className="bg-slate-50">
                        <td colSpan={2} className="py-1 pl-6 text-xs text-slate-500">↳ {mt.member_type}</td>
                        <td className="text-right text-xs">{mt.count}</td>
                        <td className="text-right text-xs">€{Number(mt.revenue || 0).toFixed(2)}</td>
                        <td colSpan={2} className="text-xs text-slate-400 pl-4">member-only</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// Per-event tier→variant breakdown. Grouped by event so multi-event ranges
// stay readable; each event heading shows the total row count for context.
function ByVariantTable({ rows, download }) {
  if (!rows || rows.length === 0) return null;
  const grouped = rows.reduce((acc, r) => {
    const key = r.event_id || "";
    if (!acc[key]) acc[key] = { name: r.event_name, list: [] };
    acc[key].list.push(r);
    return acc;
  }, {});
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="fa-analytics-by-variant">
      <div className="flex items-center justify-between mb-1"><h3 className="font-bold text-[#1e3a5f]">By tier variant</h3>{download}</div>
      <p className="text-[10px] text-slate-400 mb-3">Valid tickets sold and net revenue per variant, grouped by event (refunded, cancelled and void excluded).</p>
      {Object.entries(grouped).map(([eid, grp]) => (
        <div key={eid} className="mb-4 last:mb-0" data-testid={`fa-variant-event-${eid}`}>
          <p className="text-xs font-bold text-slate-600 mb-1">{grp.name || "(unknown event)"}</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400">
                <th className="py-1">Tier</th>
                <th>Variant</th>
                <th className="text-right">Sold</th>
                <th className="text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {grp.list.map((r, idx) => (
                <tr key={`${r.tier}-${r.variant}-${idx}`} className="border-t border-slate-100">
                  <td className="py-2">{r.tier}</td>
                  <td className="text-slate-500">{r.variant || "—"}</td>
                  <td className="text-right">{r.count}</td>
                  <td className="text-right font-bold">€{Number(r.revenue || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function FAPerEventView() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [refundDialog, setRefundDialog] = useState(null);
  const [refundPreview, setRefundPreview] = useState(null);
  const [refundBusy, setRefundBusy] = useState(false);
  const [confirmConsolidateAll, setConfirmConsolidateAll] = useState(false);
  const [consolidateBusy, setConsolidateBusy] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => { authAxios.get("/events").then(r => setEvents(r.data)); }, []);
  const load = () => {
    if (!eventId) { setData(null); return; }
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (search.trim()) params.set("search", search.trim());
    const q = params.toString() ? `?${params.toString()}` : "";
    authAxios.get(`/ticketing/fa/events/${eventId}/tickets${q}`).then(r => setData(r.data));
  };
  useEffect(() => { load(); }, [eventId, statusFilter]);

  const openRefund = async (t) => {
    setRefundDialog(t);
    setRefundPreview(null);
    try {
      const r = await authAxios.get(`/ticketing/fa/tickets/${t.id}/refund-preview`);
      setRefundPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load refund preview");
    }
  };
  const cancelWalkin = async (t) => {
    if (!window.confirm(`Cancel walk-in ticket ${t.id.slice(0, 8)}? Hand back €${t.amount_paid.toFixed(2)} (${t.payment_method || "offline"}) to the buyer.`)) return;
    try {
      await authAxios.post(`/ticketing/fa/walkin/tickets/${t.id}/cancel`);
      toast.success("Walk-in ticket cancelled — refund offline");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const confirmRefund = async () => {
    if (!refundDialog || !refundPreview) return;
    setRefundBusy(true);
    try {
      const res = await authAxios.post(`/ticketing/fa/tickets/${refundDialog.id}/refund`, {});
      const method = res.data?.method || "split";
      toast.success(`Refunded €${(res.data?.total ?? 0).toFixed(2)} via ${method}`);
      setRefundDialog(null); setRefundPreview(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setRefundBusy(false); }
  };
  const updateStatus = async (tid, status) => {
    try { await authAxios.put(`/ticketing/fa/tickets/${tid}/status`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const consolidateAllPaid = async () => {
    if (!eventId) return;
    setConsolidateBusy(true);
    try {
      const r = await authAxios.post(`/ticketing/fa/events/${eventId}/consolidate-all`);
      toast.success(r.data?.message || "Consolidated");
      setConfirmConsolidateAll(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Consolidation failed");
    } finally {
      setConsolidateBusy(false);
    }
  };

  const paidCount = data?.summary?.paid_count ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={eventId} onChange={e => setEventId(e.target.value)} className="px-3 py-2 rounded-xl border text-sm" data-testid="fa-event-select">
          <option value="">Select event</option>
          {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-xl border text-sm">
          <option value="">All statuses</option>
          <option value="paid">Paid</option><option value="used">Used</option><option value="refunded">Refunded</option><option value="cancelled">Cancelled</option><option value="consolidated">Consolidated</option>
        </select>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") load(); }}
          placeholder="Search by ticket ID / buyer / tier / order..."
          className="px-3 py-2 rounded-xl border text-sm flex-1 min-w-[200px]"
          data-testid="fa-ticket-search-input"
        />
        <button onClick={load} className="p-2 rounded-full bg-slate-100 hover:bg-slate-200" data-testid="fa-ticket-search-btn"><RefreshCw className="w-4 h-4" /></button>
        <button
          onClick={async () => {
            if (!eventId) return toast.error("Select an event first");
            try {
              const params = new URLSearchParams();
              if (statusFilter) params.set("status", statusFilter);
              if (search.trim()) params.set("search", search.trim());
              const q = params.toString() ? `?${params.toString()}` : "";
              const r = await authAxios.get(`/ticketing/fa/events/${eventId}/tickets/export${q}`, { responseType: "blob" });
              downloadBlob(r.data, `tickets_${eventId.slice(0,8)}.xlsx`);
              toast.success("Excel downloaded");
            } catch (e) { toast.error("Export failed"); }
          }}
          className="flex items-center gap-1 px-3 py-2 rounded-xl bg-[#2d8a4e] text-white text-xs font-medium"
          data-testid="fa-ticket-export-btn"
        ><Download className="w-4 h-4" /> Excel</button>
        <button
          onClick={() => {
            if (!eventId) return toast.error("Select an event first");
            if (paidCount === 0) return toast.info("No paid tickets to consolidate for this event");
            setConfirmConsolidateAll(true);
          }}
          disabled={!eventId || paidCount === 0}
          className="flex items-center gap-1 px-3 py-2 rounded-xl bg-blue-600 text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="fa-consolidate-all-btn"
        ><Lock className="w-4 h-4" /> Consolidate all paid{paidCount ? ` (${paidCount})` : ""}</button>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="fa-perevent-kpi-row">
            <Kpi label="Total" value={data.summary.total_count} />
            <Kpi label="Paid" value={data.summary.paid_count} color="green" />
            <Kpi label="Used" value={data.summary.used_count} color="slate" />
            <Kpi label="Refunded" value={data.summary.refunded_count} color="red" />
            <Kpi label="Revenue" value={`€${data.summary.gross_revenue.toFixed(2)}`} color="blue" />
          </div>
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left p-3">Ticket ID</th><th className="text-left p-3">Date</th><th className="text-left p-3">Buyer</th><th className="text-left p-3">Tier</th><th className="text-right p-3">€</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr></thead>
              <tbody>
                {data.tickets.map(t => (
                  <tr key={t.id} className="border-t border-slate-100" data-testid={`fa-ticket-row-${t.id}`}>
                    <td className="p-3 font-mono text-xs text-slate-700" title={t.id} data-testid={`fa-ticket-shortid-${t.id}`}>{(t.id || "").slice(0, 8)}</td>
                    <td className="p-3 text-xs text-slate-500">{t.purchased_at?.slice(0, 10)}</td>
                    <td className="p-3"><p className="font-medium text-[#1e3a5f]">{t.buyer_name}</p><p className="text-xs text-slate-400">{t.buyer_email}</p></td>
                    <td className="p-3">{t.tier_name}</td>
                    <td className="p-3 text-right"><RefundAmountCell t={t} testid={`fa-ticket-amount-${t.id}`} /></td>
                    <td className="p-3"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t.status}</span></td>
                    <td className="p-3 text-right">
                      {t.channel === "walkin" && ["paid", "used"].includes(t.status) && (
                        <button onClick={() => cancelWalkin(t)} className="text-xs text-red-500 hover:underline mr-2" data-testid={`walkin-cancel-${t.id}`}>Cancel walk-in</button>
                      )}
                      {t.status === "paid" && t.channel !== "walkin" && <>
                        {t.is_refundable ? (
                          <button onClick={() => openRefund(t)} className="text-xs text-red-500 hover:underline mr-2" data-testid={`refund-${t.id}`}>Refund</button>
                        ) : (
                          <span className="text-xs text-slate-400 mr-2" title={t.refund_blocked_reason || "Not refundable"}>Not refundable</span>
                        )}
                        <button onClick={() => updateStatus(t.id, "consolidated")} className="text-xs text-blue-500 hover:underline">Consolidate</button>
                      </>}
                      {t.status === "used" && <span className="text-xs text-slate-400">Used — non-refundable</span>}
                    </td>
                  </tr>
                ))}
                {data.tickets.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No tickets</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!data && eventId === "" && <p className="text-slate-400 text-center py-8">Select an event to see tickets</p>}

      {refundDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { if (!refundBusy) { setRefundDialog(null); setRefundPreview(null); } }}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-6 max-w-sm w-full" data-testid="fa-refund-dialog">
            <h3 className="font-bold text-[#1e3a5f] mb-2">Refund €{refundDialog.amount_paid.toFixed(2)}</h3>
            {!refundPreview ? (
              <p className="text-sm text-slate-500 py-4">Loading refund preview…</p>
            ) : (
              <>
                <p className="text-sm text-slate-600 mb-3">Refund follows the original payment method — every euro goes back to where it came from:</p>
                <div className="space-y-1.5 mb-4 text-sm">
                  {(refundPreview.breakdown?.stripe || 0) > 0 && (
                    <div className="flex justify-between bg-blue-50 border border-blue-100 rounded-xl px-3 py-2" data-testid="refund-preview-stripe">
                      <span className="text-blue-800">Card refund (Stripe)</span>
                      <span className="font-bold text-blue-800">€{refundPreview.breakdown.stripe.toFixed(2)}</span>
                    </div>
                  )}
                  {(refundPreview.breakdown?.wallet || 0) > 0 && (
                    <div className="flex justify-between bg-green-50 border border-green-100 rounded-xl px-3 py-2" data-testid="refund-preview-wallet">
                      <span className="text-green-800">Wallet credit</span>
                      <span className="font-bold text-green-800">€{refundPreview.breakdown.wallet.toFixed(2)}</span>
                    </div>
                  )}
                  {(refundPreview.breakdown?.voucher || 0) > 0 && (
                    <div className="flex justify-between bg-amber-50 border border-amber-100 rounded-xl px-3 py-2" data-testid="refund-preview-voucher">
                      <span className="text-amber-800">Shop voucher</span>
                      <span className="font-bold text-amber-800">€{refundPreview.breakdown.voucher.toFixed(2)}</span>
                    </div>
                  )}
                  {refundPreview.total <= 0 && (
                    <p className="text-red-600 text-xs">Nothing to refund — this ticket has no recorded payment sources.</p>
                  )}
                </div>
                {refundPreview.inferred_source && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 mb-3" data-testid="refund-preview-legacy-note">
                    Legacy ticket — split reconstructed from {refundPreview.inferred_source.replace(/_/g, " ")}. Verify before confirming.
                  </p>
                )}
              </>
            )}
            <div className="space-y-2">
              <button
                onClick={confirmRefund}
                disabled={!refundPreview || refundBusy || (refundPreview?.total || 0) <= 0}
                className="w-full py-3 rounded-full bg-[#2d8a4e] text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="fa-refund-confirm-btn"
              >
                {refundBusy ? "Refunding…" : `Confirm refund €${(refundPreview?.total ?? refundDialog.amount_paid).toFixed(2)}`}
              </button>
              <button
                onClick={() => { setRefundDialog(null); setRefundPreview(null); }}
                disabled={refundBusy}
                className="w-full py-3 rounded-full bg-slate-100 text-slate-600 font-medium"
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={confirmConsolidateAll} onOpenChange={setConfirmConsolidateAll}>
        <AlertDialogContent data-testid="fa-consolidate-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Consolidate {paidCount} paid ticket{paidCount === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This flags every remaining <b>paid</b> ticket for this event as <b>consolidated</b> — a book-close marker. No money moves and no capacity is released, but the Refund and Consolidate buttons on those rows will disappear. Use this only once you have reconciled the event&apos;s P&amp;L.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={consolidateBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={consolidateAllPaid} disabled={consolidateBusy} data-testid="fa-consolidate-all-confirm">
              {consolidateBusy ? "Consolidating..." : `Consolidate ${paidCount}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Kpi = ({ label, value, color = "slate" }) => {
  const colors = { slate: "bg-slate-50 text-slate-700", green: "bg-green-50 text-green-700", red: "bg-red-50 text-red-700", blue: "bg-blue-50 text-blue-700", amber: "bg-amber-50 text-amber-700" };
  return (
    <div className={`p-4 rounded-2xl ${colors[color]}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-60">{label}</p>
      <p className="text-2xl font-extrabold mt-1">{value}</p>
    </div>
  );
};
